<script setup>
import {TicCardPrefixList} from "@/graphql/queries/hpo/ticCardPrefixList.graphql";
import {useQuery} from "@vue/apollo-composable";
import {useLastResult} from "@/graphql/composable";
import {computed, ref, watch} from "vue";
import * as yup from "yup";
import {useDjangoForm} from "@/utils/forms";
import {useDisplay} from "vuetify/framework";
import TicKeypad from "@/components/hpo/patientModal/tabs/TicKeypad.vue";
import PrefixModal from "@/components/hpo/patientModal/tabs/triage/PrefixModal.vue";
import {mdiPlus} from "@mdi/js";
import {ticSchema} from "@/components/hpo/triage/schema";
import QrScanButton from "../QrScanButton.vue";

const props = defineProps({
  hpoId: {
    type: String,
    required: true
  },
  patientId: {
    type: Number,
    default: null,
  },
  patientList: {
    type: Array,
    default: () => [],
  },
});

const ticPrefixModel = defineModel('ticPrefix');
const ticNumberModel = defineModel('ticNumber');

const emit = defineEmits(['submit']);

const {smAndDown} = useDisplay();
const showPrefixModal = ref(false);

const schema = yup.object({
  ...ticSchema,
});

const {defineField, handleSubmit, isFieldValid, meta, errors} = useDjangoForm({
  validationSchema: schema,
  initialValues: {
    ticPrefix: ticPrefixModel.value || '',
    ticNumber: ticNumberModel.value || '',
  },
});

const [ticPrefix, ticPrefixProps] = defineField('ticPrefix');
const [ticNumber, ticNumberProps] = defineField('ticNumber');

const {result} = useQuery(TicCardPrefixList, {hpoId: props.hpoId});
const prefixOptions = useLastResult(result, [], data => data.ticCardPrefixList.list);

watch(prefixOptions, async (newValue, previousValue) => {
  const options = Object.values(newValue);
  if (options.length > 0 && !ticPrefix.value) {
    ticPrefix.value = options[0];
  }
}, {immediate: true});

function isSelected(letter) {
  return ticPrefix.value === letter;
}

function selectLetter(letter) {
  ticPrefix.value = letter;
}

const scannedValue = ref(null)

function handleScan(value) {
  ticPrefix.value = value.ticPrefix;
  ticNumber.value = value.ticNumber;
  onSubmit()
}

const onSubmit = handleSubmit(values => {
  ticPrefixModel.value = ticPrefix.value;
  ticNumberModel.value = ticNumber.value;

  emit('submit', {
    tic: `${ticPrefix.value}${ticNumber.value}`,
    ticPrefix: ticPrefix.value,
    ticNumber: ticNumber.value,
  });
});

const patientExists = computed(() => {
  if (!ticPrefix.value || !ticNumber.value || ticNumber.value.length !== 4) {
    return false;
  }

  for (const patient of props.patientList) {
    if (patient.ticPrefix === ticPrefix.value && patient.ticNumber === ticNumber.value && patient.id !== props.patientId) {
      return true;
    }
  }

  return false;
});

const allowContinue = computed(() => {
  return isFieldValid('ticPrefix') && isFieldValid('ticNumber') && (props.patientId === null || !patientExists.value);
});
</script>

<template>
  <section class="tic-entry-screen">
    <header class="title">
      <h2>{{ pgettext('label', 'TIC Card Number') }}</h2>
    </header>

    <aside class="tic-entry-screen__scanner">

    </aside>
    <main class="tic-entry-screen__form">
      <form @submit.prevent="onSubmit">
        <div class="tic-entry-screen__prefix">
          <div class="tic-entry-screen__prefix-letters d-flex ga-2 flex-wrap">
            <v-btn v-for="letter in prefixOptions" :key="'k' + letter"
                   type="button"
                   class="tic-entry-screen__prefix-button"
                   rounded="sm"
                   :variant="isSelected(letter) ? 'flat' : 'tonal'"
                   :size="smAndDown ? 'small' : 'large'"
                   :height="smAndDown ? 44 : 52"
                   :ripple="false"
                   @click="selectLetter(letter)">
              {{ letter }}
            </v-btn>
          </div>

          <v-btn @click="showPrefixModal = true" :prepend-icon="mdiPlus">
            {{ pgettext('action', 'Add new letter') }}
          </v-btn>
        </div>

        <div class="tic-entry-screen__number">
          <div class="tic-entry-screen__number-input-prefix">
            {{ ticPrefix }}
          </div>
          <v-otp-input v-model="ticNumber" v-bind="ticNumberProps" :length="4" focus-all variant="underlined"
                       class="tic-entry-screen__number-input">
          </v-otp-input>
        </div>

        <QrScanButton @scan="handleScan"/>

        <div class="tic-entry-screen__messages">
          <v-alert v-if="patientExists" type="warning" variant="tonal" density="comfortable">
            <strong>
              {{ interpolate(gettext('Patient %(tic)s was already added.'), {tic: `${ticPrefix}${ticNumber}`}) }}
            </strong>
          </v-alert>
        </div>

        <div class="tic-entry-screen__actions">
          <v-btn type="submit" color="green" size="large"
                 block
                 :disabled="!allowContinue">
            <strong>{{ pgettext('action', 'Continue') }}</strong>
          </v-btn>
        </div>
      </form>
    </main>
    <aside class="tic-entry-screen__keypad">
      <TicKeypad v-model="ticNumber"/>
    </aside>
  </section>

  <PrefixModal v-if="showPrefixModal"
               :hpoId="hpoId"
               @close="showPrefixModal = false"/>
</template>
